import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { getCustomerMarketDataSnapshotRequestKey } from '../../../../../hooks/useMultiCustomerMarketDataSnapshot';
import { baseColumn } from '../../baseColumn';
import type { ColDefFactory, Column } from '../../types';
import { SecurityMarketDataCell } from '../SecurityMarketDataCell';
import {
  getMarketDataSnapshotForRequestKey,
  getMarketDataSnapshotSizeBucketLevel,
  toSecurityMarketDataCellValue,
  tryReadSecurityMarketDataRow,
} from '../helpers';
import type { CustomerSecurityMarketDataColumnParams, SecurityMarketDataCellValue } from '../types';

function getCustomerSecurityMarketDataCellValue(
  column: Column<CustomerSecurityMarketDataColumnParams>,
  node: ValueGetterParams['node']
): SecurityMarketDataCellValue {
  const row = tryReadSecurityMarketDataRow(column, node);
  if (!row) {
    return undefined;
  }

  const { data, symbol, marketDataCollectionPath } = row;
  const requestParams = column.params?.getRequestParams?.({
    data,
    symbol,
  });
  if (!requestParams?.Counterparty) {
    return undefined;
  }

  const { Counterparty, ...customerRequestParams } = requestParams;
  const requestKey = getCustomerMarketDataSnapshotRequestKey({
    Symbol: symbol,
    Counterparty,
    ...customerRequestParams,
  });
  const snapshot = getMarketDataSnapshotForRequestKey(data, marketDataCollectionPath, requestKey);
  const sizeBucketLevel = getMarketDataSnapshotSizeBucketLevel(data, snapshot, column.params?.sizeBucketField);
  if (column.params?.sizeBucketField && !sizeBucketLevel) {
    return undefined;
  }
  return toSecurityMarketDataCellValue(snapshot, sizeBucketLevel);
}

export const customerSecurityMarketData: ColDefFactory<Column<CustomerSecurityMarketDataColumnParams>> = column => ({
  ...baseColumn(column),
  valueGetter: ({ node }: ValueGetterParams<unknown>): SecurityMarketDataCellValue =>
    getCustomerSecurityMarketDataCellValue(column, node),
  cellRenderer: (params: ICellRendererParams<unknown, SecurityMarketDataCellValue>) => (
    <SecurityMarketDataCell {...params} />
  ),
});
